import { OrganisationListResponseV1 } from "@ucchub/ui/src/apiTypes/organisation";
import environment from "environment";
import { BaseService } from "@ucchub/ui/src/BaseService";
import { useState } from "react";
import { t } from "i18next";
import { unescapeObjectValues } from "@ucchub/ui";
import { CustomerListItem } from "@ucchub/ui/src/cloudNumbers/types/Customer.types";
import toArrayQueryString from "@ucchub/ui/src/utils/queryStringWithArray";

const KEYS_TO_BE_UNESCAPED: (keyof OrganisationListResponseV1)[] = [
  "contactEmail",
  "country",
  "name",
];

const API_FIELDS = ["country", "products", "status"];

const API_FIELDS_QUERY = API_FIELDS.map((field) => `fields[]=${field}`).join(
  "&"
);

type FetchCustomerListParams = {
  search?: string;
  page: number;
};

const useCustomerList = () => {
  const [customerList, setCustomerList] = useState<CustomerListItem[]>();

  const [error, setError] = useState<string>();
  const [isCustomerListLoading, setIsCustomerListLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePagesToFetch, setHasMorePagesToFetch] = useState(false);

  const fetchCustomerList = ({ search, page }: FetchCustomerListParams) => {
    setIsCustomerListLoading(true);
    setError("");

    const queryParams = toArrayQueryString({
      search,
      page,
    });

    const apiUrl = `${environment.sharedApi.customer}?${API_FIELDS_QUERY}&${queryParams}`;

    BaseService.get<OrganisationListResponseV1[]>(apiUrl)
      .then(({ data }) => {
        if (data.length > 0) {
          setCustomerList((customerListOld) => {
            const newCustomerList = data.map((customer) =>
              unescapeObjectValues(customer, KEYS_TO_BE_UNESCAPED)
            );

            const oldList =
              page === 0 || !customerListOld ? [] : customerListOld;

            setCurrentPage(page);
            return [...oldList, ...newCustomerList];
          });

          /* For pagination, we don't know without doing extra queries. 
            to find out if there is more data, we need to ask for the next page. 
            If we an empty array that means there no more items to load */
          setHasMorePagesToFetch(true);
        } else {
          setHasMorePagesToFetch(false);
        }
      })

      .catch(() => {
        setError(t("core:customers:fetch_failure_list") || undefined);
      })
      .finally(() => {
        setIsCustomerListLoading(false);
      });
  };

  const clearCustomerList = () => {
    setCustomerList([]);
    setCurrentPage(0);
  };

  return {
    customerList,
    error,
    isCustomerListLoading,
    fetchCustomerList,
    hasMorePagesToFetch,
    clearCustomerList,
    currentPage,
  };
};

export default useCustomerList;
